import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import { formatDateToString } from "../../_base/helper/FunctionHelper";
import { uploadImageAddFolder } from "../../api/ApiMore";

// Ảnh chọn từ máy chỉ hiện xem trước bằng URL blob:, chưa tải lên CDN.
// Giữ File theo URL đó; bấm Lưu block mới tải lên CDN rồi thay URL blob: trong config bằng URL CDN.
const pendingFiles = new Map<string, File>();
// Ảnh đã lên CDN nhưng lưu block lỗi: lần lưu sau dùng lại URL, không tải lần nữa
const uploadedUrls = new Map<string, string>();

export const previewImage = (file: File) => {
  const url = URL.createObjectURL(file);
  pendingFiles.set(url, file);
  return url;
};

const isBlobUrl = (value: unknown): value is string =>
  typeof value === "string" && value.startsWith("blob:");

// Ảnh nằm ở nhiều cấp của config (slide, banner phụ, icon...) nên duyệt hết để tìm URL blob:
const collectBlobUrls = (value: unknown, found: Set<string>): Set<string> => {
  if (isBlobUrl(value)) found.add(value);
  else if (Array.isArray(value)) value.forEach((v) => collectBlobUrls(v, found));
  else if (value && typeof value === "object") {
    Object.values(value).forEach((v) => collectBlobUrls(v, found));
  }
  return found;
};

export const replaceImageUrls = (value: unknown, urls: Map<string, string>): unknown => {
  if (isBlobUrl(value)) return urls.get(value) ?? value;
  if (Array.isArray(value)) return value.map((v) => replaceImageUrls(v, urls));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, v]) => [key, replaceImageUrls(v, urls)])
    );
  }
  return value;
};

// Gửi mọi ảnh trong một request Upload/files (field "files" lặp lại), thư mục cdn/web/DDMMYYYY.
// API trả data[] theo đúng thứ tự file gửi lên.
const uploadAll = async (files: File[]): Promise<string[] | null> => {
  const res = await uploadImageAddFolder(`cdn/web/${formatDateToString(new Date())}`, files);
  const urls: unknown[] = Array.isArray(res?.data) ? res.data.map((item: any) => item?.url) : [];
  if (res?.success && urls.length === files.length && urls.every((u) => typeof u === "string" && u)) {
    return urls as string[];
  }
  // res rỗng: lỗi mạng / hết phiên đã có thông báo chung
  if (res) NotificationExtension.Fails(res.message ?? "Tải ảnh lên CDN thất bại");
  return null;
};

// Tải lên CDN mọi ảnh mới chọn trong config, trả bảng URL blob: → URL CDN.
// Tải lỗi thì trả null để chưa lưu block.
export const uploadPendingImages = async (config: unknown): Promise<Map<string, string> | null> => {
  const blobs = Array.from(collectBlobUrls(config, new Set()));
  // URL blob: đã lưu từ trước khi có bước tải ảnh: file không còn trên máy, không tải được
  const lost = blobs.filter((b) => !pendingFiles.has(b) && !uploadedUrls.has(b));
  if (lost.length > 0) {
    NotificationExtension.Warn(
      `Có ${lost.length} ảnh tạm cũ không còn trên máy nên không hiện trên web. Bấm vào ảnh đó để chọn lại.`
    );
  }

  const pending = blobs.filter((b) => pendingFiles.has(b));
  if (pending.length > 0) {
    const cdnUrls = await uploadAll(pending.map((b) => pendingFiles.get(b) as File));
    if (!cdnUrls) return null;
    pending.forEach((blob, i) => {
      uploadedUrls.set(blob, cdnUrls[i]);
      pendingFiles.delete(blob);
    });
  }

  const urls = new Map<string, string>();
  blobs.forEach((b) => {
    const url = uploadedUrls.get(b);
    if (url) urls.set(b, url);
  });
  return urls;
};
